import React, { useState, useEffect, useMemo, useCallback } from "react"
import Button from "@mui/material/Button"
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded"
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded"
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded"
import Alert from "@mui/material/Alert"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Typography from "@mui/material/Typography"
import FormControl from "@mui/material/FormControl"
import InputLabel from "@mui/material/InputLabel"
import Select from "@mui/material/Select"
import MenuItem from "@mui/material/MenuItem"
import FormHelperText from "@mui/material/FormHelperText"
import PageHeading from "components/common/LayoutComponents/PageHeading"
import LoadingScreen from "components/common/LoadingScreen"
import { PERMISSIONS } from "constants/permission"
import { UsePermissionVerification } from "components/customHooks"
import { listAiAgents, listAvailableLlmModels, listAvailableRealtimeModels } from "services/aiAgent"
import { verifyResponse } from "utils/httpRequest"
import AiAgentTable from "./AiAgentTable"
import useAiAgentDialog from "./AiAgentDlg"
import AiAgentDetail from "./AiAgentDetail"
import { StyledAiAgent } from "./AiAgent.style"
import { getTierPrograms } from "services/tierProgram"
import { getProducts } from "services/products"
import {
  getSystemConfigByKey,
  editSystemConfigs,
  addSystemConfigs,
} from "services/systemConfig"
import { useSnackbar } from "notistack"
import {
  mergeLlmCatalog,
  extractLlmCombinedFromResponse,
  extractLlmSourceFromResponse,
} from "utils/llmCatalogMerge"

const STUDY_LAB_CONFIG_KEY = "study_lab_model_config"
const VOICE_REALTIME_CONFIG_KEY = "voice_realtime_model_config"

const STUDY_LAB_MODEL_DEFAULTS = {
  enabled: false,
  primaryModel: "gpt-5.2",
  fallbackModel: "gpt-5.2",
  backendTimeoutMs: 45000,
  retryTimeoutMs: 30000,
}

const VOICE_REALTIME_MODEL_DEFAULTS = {
  primaryModel: "gpt-realtime",
  fallbackModel: "gpt-realtime-mini",
  transcriptionModel: "gpt-4o-transcribe",
  configVersion: 1,
}

const AiAgent = () => {
  const { enqueueSnackbar } = useSnackbar()
  const [aiAgents, setAiAgents] = useState([])
  const [aiAgentLoading, setAiAgentLoading] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [tierPrograms, setTierPrograms] = useState([])
  const [products, setProducts] = useState([])
  const [studyLabConfigRecord, setStudyLabConfigRecord] = useState(null)
  const [studyLabModelDraft, setStudyLabModelDraft] = useState(() => ({
    ...STUDY_LAB_MODEL_DEFAULTS,
  }))
  const [llmCatalogModels, setLlmCatalogModels] = useState([])
  const [llmCatalogSource, setLlmCatalogSource] = useState({ openaiLive: true, anthropicLive: true })
  const [savingStudyLabModels, setSavingStudyLabModels] = useState(false)
  const [voiceRealtimeConfigRecord, setVoiceRealtimeConfigRecord] = useState(null)
  const [voiceRealtimeModelDraft, setVoiceRealtimeModelDraft] = useState(() => ({
    ...VOICE_REALTIME_MODEL_DEFAULTS,
  }))
  const [realtimeCatalogModels, setRealtimeCatalogModels] = useState([])
  const [realtimeCatalogLive, setRealtimeCatalogLive] = useState(true)
  const [savingVoiceRealtimeModels, setSavingVoiceRealtimeModels] = useState(false)

  const AccessControl = UsePermissionVerification()

  useEffect(() => {
    AccessControl.SetAccess({
      ...AccessControl.Access,
      listAiAgentAccess: AccessControl.VerifyAccess([
        PERMISSIONS.AI_AGENT.AI_AGENT_LIST,
      ]),
      addAiAgentAccess: AccessControl.VerifyAccess([
        PERMISSIONS.AI_AGENT.AI_AGENT_ADD,
      ]),
      getDetailAiAgentAccess: AccessControl.VerifyAccess([
        PERMISSIONS.AI_AGENT.AI_AGENT_DETAIL,
      ]),
      editAiAgentAccess: AccessControl.VerifyAccess([
        PERMISSIONS.AI_AGENT.AI_AGENT_EDIT,
      ]),
      deleteAiAgentAccess: AccessControl.VerifyAccess([
        PERMISSIONS.AI_AGENT.AI_AGENT_DELETE,
      ]),
      toggleStatusAiAgentAccess: AccessControl.VerifyAccess([
        PERMISSIONS.AI_AGENT.AI_AGENT_TOGGLE_STATUS,
      ]),
      AI_AGENT_KNOWLEDGE_BASE_ADD: AccessControl.VerifyAccess([
        PERMISSIONS.AI_AGENT.AI_AGENT_KNOWLEDGE_BASE_ADD,
      ]),
      AI_AGENT_KNOWLEDGE_BASE_DELETE: AccessControl.VerifyAccess([
        PERMISSIONS.AI_AGENT.AI_AGENT_KNOWLEDGE_BASE_DELETE,
      ]),
      editStudyLabGlobalModelsAccess: AccessControl.VerifyAccess([
        PERMISSIONS.AI_AGENT.AI_AGENT_EDIT,
      ]),
    })
  }, [AccessControl.UserPermissions])

  const studyLabGlobalMenuModels = useMemo(() => {
    const map = new Map((llmCatalogModels || []).map((m) => [m.id, m]))
    ;[studyLabModelDraft?.primaryModel, studyLabModelDraft?.fallbackModel].forEach(
      (raw) => {
        const id = (raw || "").trim()
        if (id && !map.has(id)) map.set(id, { id, provider: "saved" })
      }
    )
    return Array.from(map.values())
  }, [
    llmCatalogModels,
    studyLabModelDraft?.primaryModel,
    studyLabModelDraft?.fallbackModel,
  ])

  const voiceRealtimeMenuModels = useMemo(() => {
    const map = new Map()
    ;(realtimeCatalogModels || []).forEach((m) => {
      const id = String(m?.id || "").trim()
      if (id) map.set(id, { id, kind: m.kind || "realtime" })
    })
    ;[
      voiceRealtimeModelDraft?.primaryModel,
      voiceRealtimeModelDraft?.fallbackModel,
      voiceRealtimeModelDraft?.transcriptionModel,
    ].forEach((raw) => {
      const id = (raw || "").trim()
      if (id && !map.has(id)) map.set(id, { id, kind: "saved" })
    })
    return Array.from(map.values())
  }, [
    realtimeCatalogModels,
    voiceRealtimeModelDraft?.primaryModel,
    voiceRealtimeModelDraft?.fallbackModel,
    voiceRealtimeModelDraft?.transcriptionModel,
  ])

  const voiceRealtimeSpeechModels = useMemo(
    () => voiceRealtimeMenuModels.filter((m) => m.kind !== "transcription"),
    [voiceRealtimeMenuModels]
  )

  const voiceRealtimeTranscriptionModels = useMemo(
    () => voiceRealtimeMenuModels.filter((m) => m.kind === "transcription" || m.kind === "saved"),
    [voiceRealtimeMenuModels]
  )

  const loadLlmCatalog = useCallback(async () => {
    try {
      const raw = await listAvailableLlmModels()
      verifyResponse(raw)
      setLlmCatalogModels(mergeLlmCatalog(extractLlmCombinedFromResponse(raw)))
      setLlmCatalogSource(extractLlmSourceFromResponse(raw))
    } catch (e) {
      console.warn("LLM catalog load failed", e)
      setLlmCatalogModels(mergeLlmCatalog([]))
      setLlmCatalogSource({ openaiLive: false, anthropicLive: false })
    }
  }, [])

  const loadRealtimeCatalog = useCallback(async () => {
    try {
      const raw = await listAvailableRealtimeModels()
      verifyResponse(raw)
      const payload = raw?.data || raw
      const combined = Array.isArray(payload?.combined) ? payload.combined : []
      setRealtimeCatalogModels(combined)
      setRealtimeCatalogLive(Boolean(payload?.openaiLive))
    } catch (e) {
      console.warn("Realtime catalog load failed", e)
      setRealtimeCatalogModels([])
      setRealtimeCatalogLive(false)
    }
  }, [])

  const reloadStudyLabModelConfig = useCallback(async () => {
    try {
      const response = await getSystemConfigByKey(STUDY_LAB_CONFIG_KEY)
      verifyResponse(response)
      const item = response?.data || null
      setStudyLabConfigRecord(item)
      const parsed =
        item?.configValueJson && typeof item.configValueJson === "object"
          ? item.configValueJson
          : {}
      setStudyLabModelDraft({
        ...STUDY_LAB_MODEL_DEFAULTS,
        ...parsed,
      })
    } catch (error) {
      console.error("Error loading Study Lab model config:", error)
      setStudyLabConfigRecord(null)
      setStudyLabModelDraft({ ...STUDY_LAB_MODEL_DEFAULTS })
    }
  }, [])

  const reloadVoiceRealtimeModelConfig = useCallback(async () => {
    try {
      const response = await getSystemConfigByKey(VOICE_REALTIME_CONFIG_KEY)
      verifyResponse(response)
      const item = response?.data || null
      setVoiceRealtimeConfigRecord(item)
      const parsed =
        item?.configValueJson && typeof item.configValueJson === "object"
          ? item.configValueJson
          : {}
      setVoiceRealtimeModelDraft({
        ...VOICE_REALTIME_MODEL_DEFAULTS,
        ...parsed,
      })
    } catch (error) {
      console.error("Error loading Voice Realtime model config:", error)
      setVoiceRealtimeConfigRecord(null)
      setVoiceRealtimeModelDraft({ ...VOICE_REALTIME_MODEL_DEFAULTS })
    }
  }, [])

  const saveStudyLabModels = async () => {
    if (!AccessControl.Access.editStudyLabGlobalModelsAccess) return
    setSavingStudyLabModels(true)
    try {
      const payload = {
        name: studyLabConfigRecord?.name || "Study Lab Model Config",
        configKey: STUDY_LAB_CONFIG_KEY,
        description:
          studyLabConfigRecord?.description ||
          "Runtime model, timeout, and availability config for Study Lab generation.",
        configValue: JSON.stringify({
          ...studyLabModelDraft,
        }),
      }
      let response
      if (studyLabConfigRecord?.id) {
        response = await editSystemConfigs({
          ...payload,
          id: studyLabConfigRecord.id,
        })
      } else {
        response = await addSystemConfigs(payload)
      }
      verifyResponse(response)
      enqueueSnackbar("Study Lab model settings saved.", { variant: "success" })
      await reloadStudyLabModelConfig()
    } catch (e) {
      enqueueSnackbar("Failed to save Study Lab model settings.", {
        variant: "error",
      })
    } finally {
      setSavingStudyLabModels(false)
    }
  }

  const saveVoiceRealtimeModels = async () => {
    if (!AccessControl.Access.editStudyLabGlobalModelsAccess) return
    setSavingVoiceRealtimeModels(true)
    try {
      const nextVersion =
        Number(voiceRealtimeModelDraft?.configVersion || voiceRealtimeConfigRecord?.configValueJson?.configVersion || 1) + 1
      const payload = {
        name: voiceRealtimeConfigRecord?.name || "Voice Realtime Model Config",
        configKey: VOICE_REALTIME_CONFIG_KEY,
        description:
          voiceRealtimeConfigRecord?.description ||
          "Global OpenAI Realtime speech + transcription models for coach voice chat.",
        configValue: JSON.stringify({
          ...voiceRealtimeModelDraft,
          configVersion: nextVersion,
        }),
      }
      let response
      if (voiceRealtimeConfigRecord?.id) {
        response = await editSystemConfigs({
          ...payload,
          id: voiceRealtimeConfigRecord.id,
        })
      } else {
        response = await addSystemConfigs(payload)
      }
      verifyResponse(response)
      enqueueSnackbar("Voice Realtime model settings saved.", { variant: "success" })
      await reloadVoiceRealtimeModelConfig()
    } catch (e) {
      enqueueSnackbar("Failed to save Voice Realtime model settings.", {
        variant: "error",
      })
    } finally {
      setSavingVoiceRealtimeModels(false)
    }
  }

  const { loading: aiAgentDialogLoading, openDialog, dialog: aiAgentDialog } =
    useAiAgentDialog({
      onSubmit: () => {
        loadAiAgents()
      },
    })

  const closeAiAgentDetail = () => {
    setSelectedAgent(null)
  }

  const showAiAgentDetail = (agentItem) => {
    setSelectedAgent(agentItem)
  }

  const loadAiAgents = async () => {
    setAiAgentLoading(true)
    try {
      const response = await listAiAgents({ page: 1, pageSize: 500 })
      verifyResponse(response)
      const list = response?.data?.list || response?.data || []
      setAiAgents(
        (Array.isArray(list) ? list : []).map((a) => ({
          ...a,
          isActive: Number(a?.isActive ?? a?.is_active ?? 0),
        }))
      )
    } catch (error) {
      console.error("Error loading AI agents:", error)
      setAiAgents([])
    } finally {
      setAiAgentLoading(false)
    }
  }

  useEffect(() => {
    loadAiAgents()
  }, [])

  useEffect(() => {
    reloadStudyLabModelConfig()
  }, [reloadStudyLabModelConfig])

  useEffect(() => {
    reloadVoiceRealtimeModelConfig()
  }, [reloadVoiceRealtimeModelConfig])

  useEffect(() => {
    loadLlmCatalog()
  }, [loadLlmCatalog])

  useEffect(() => {
    loadRealtimeCatalog()
  }, [loadRealtimeCatalog])

  useEffect(() => {
    const loadAccessLookups = async () => {
      try {
        const resp = await getTierPrograms()
        const arr = resp?.data || resp
        setTierPrograms(Array.isArray(arr) ? arr : [])
      } catch {
        setTierPrograms([])
      }
      try {
        const prod = await getProducts()
        setProducts(Array.isArray(prod) ? prod : [])
      } catch {
        setProducts([])
      }
    }
    loadAccessLookups()
  }, [])

  if (aiAgentLoading) {
    return <LoadingScreen />
  }

  return (
    <StyledAiAgent>
      <PageHeading>
        <div className="heading-copy">
          <h1 className="page-title">AI Agent Management</h1>
          <p className="page-subtitle">
            Configure AI coaches, access tiers, voice defaults, and knowledge
            base content.
          </p>
        </div>
        <div className="actions">
          {selectedAgent && (
            <Button
              className="back-btn"
              variant="outlined"
              size="medium"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={closeAiAgentDetail}
            >
              Back
            </Button>
          )}
          {AccessControl.Access.addAiAgentAccess && (
            <Button
              className="new-btn"
              variant="contained"
              size="medium"
              startIcon={<AddCircleOutlineRoundedIcon />}
              onClick={() => {
                openDialog()
              }}
            >
              Add AI Agent
            </Button>
          )}
          {AccessControl.Access.addAiAgentAccess && (
            <Button
              className="secondary-btn"
              variant="outlined"
              size="medium"
              startIcon={<PlaylistAddRoundedIcon />}
              onClick={async () => {
                try {
                  await (await import("services/aiAgent")).addAllFromVoices()
                  loadAiAgents()
                } catch {}
              }}
            >
              Add All
            </Button>
          )}
        </div>
      </PageHeading>

      <div className="page-surface">
        <Box className="config-alert">
          <Alert severity="info" variant="outlined">
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Study Lab Runtime Model Config
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1.5 }}>
              Models are loaded from OpenAI and Anthropic APIs (requires API keys on the server).
              {!studyLabConfigRecord?.id &&
                " This row is not in System Settings yet — saving will create it."}
            </Typography>
            {(!llmCatalogSource.openaiLive || !llmCatalogSource.anthropicLive) && (
              <Alert severity="warning" variant="outlined" sx={{ mb: 1.5, py: 0.5 }}>
                {!llmCatalogSource.openaiLive && !llmCatalogSource.anthropicLive
                  ? "Could not reach OpenAI or Anthropic APIs — showing hardcoded fallback model list. Check server API keys and network access."
                  : !llmCatalogSource.openaiLive
                  ? "Could not reach OpenAI API — OpenAI models are from the hardcoded fallback list."
                  : "Could not reach Anthropic API — Anthropic models are from the hardcoded fallback list."}
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="global-sl-primary">Primary model</InputLabel>
                <Select
                  labelId="global-sl-primary"
                  label="Primary model"
                  value={studyLabModelDraft.primaryModel || ""}
                  onChange={(e) =>
                    setStudyLabModelDraft((d) => ({
                      ...d,
                      primaryModel: e.target.value,
                    }))
                  }
                >
                  {(studyLabGlobalMenuModels || []).map((m) => (
                    <MenuItem key={`gp-${m.id}`} value={m.id}>
                      {m.provider}: {m.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="global-sl-fallback">Fallback model</InputLabel>
                <Select
                  labelId="global-sl-fallback"
                  label="Fallback model"
                  value={studyLabModelDraft.fallbackModel || ""}
                  onChange={(e) =>
                    setStudyLabModelDraft((d) => ({
                      ...d,
                      fallbackModel: e.target.value,
                    }))
                  }
                >
                  {(studyLabGlobalMenuModels || []).map((m) => (
                    <MenuItem key={`gf-${m.id}`} value={m.id}>
                      {m.provider}: {m.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box className="config-chip-wrap" sx={{ display: "flex", flexWrap: "wrap", gap: 1, alignItems: "center" }}>
                <Chip
                  size="small"
                  label={`Backend timeout: ${studyLabModelDraft.backendTimeoutMs ?? "n/a"}ms`}
                />
                <Chip
                  size="small"
                  label={`Retry timeout: ${studyLabModelDraft.retryTimeoutMs ?? "n/a"}ms`}
                />
              </Box>
            </Box>
            <FormHelperText sx={{ mt: 1 }}>
              Edit timeouts under System Settings or AI Study Lab if you need to change them.
            </FormHelperText>
            {AccessControl.Access.editStudyLabGlobalModelsAccess && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={savingStudyLabModels}
                  onClick={saveStudyLabModels}
                >
                  {savingStudyLabModels ? "Saving…" : "Save global models"}
                </Button>
              </Box>
            )}
          </Alert>
        </Box>

        <Box className="config-alert" sx={{ mt: 2 }}>
          <Alert severity="info" variant="outlined">
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Voice Realtime Models (OpenAI v2)
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1.5 }}>
              Global speech + input transcription models for all coach voice chat sessions.
              {!voiceRealtimeConfigRecord?.id &&
                " This row is not in System Settings yet — saving will create it."}
            </Typography>
            {!realtimeCatalogLive && (
              <Alert severity="warning" variant="outlined" sx={{ mb: 1.5, py: 0.5 }}>
                Could not reach OpenAI API — showing hardcoded fallback realtime model list.
              </Alert>
            )}
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="global-vr-primary">Primary model</InputLabel>
                <Select
                  labelId="global-vr-primary"
                  label="Primary model"
                  value={voiceRealtimeModelDraft.primaryModel || ""}
                  onChange={(e) =>
                    setVoiceRealtimeModelDraft((d) => ({
                      ...d,
                      primaryModel: e.target.value,
                    }))
                  }
                >
                  {(voiceRealtimeSpeechModels || []).map((m) => (
                    <MenuItem key={`vrp-${m.id}`} value={m.id}>
                      {m.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="global-vr-fallback">Fallback model</InputLabel>
                <Select
                  labelId="global-vr-fallback"
                  label="Fallback model"
                  value={voiceRealtimeModelDraft.fallbackModel || ""}
                  onChange={(e) =>
                    setVoiceRealtimeModelDraft((d) => ({
                      ...d,
                      fallbackModel: e.target.value,
                    }))
                  }
                >
                  {(voiceRealtimeSpeechModels || []).map((m) => (
                    <MenuItem key={`vrf-${m.id}`} value={m.id}>
                      {m.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel id="global-vr-transcription">Transcription model</InputLabel>
                <Select
                  labelId="global-vr-transcription"
                  label="Transcription model"
                  value={voiceRealtimeModelDraft.transcriptionModel || ""}
                  onChange={(e) =>
                    setVoiceRealtimeModelDraft((d) => ({
                      ...d,
                      transcriptionModel: e.target.value,
                    }))
                  }
                >
                  {(voiceRealtimeTranscriptionModels || []).map((m) => (
                    <MenuItem key={`vrt-${m.id}`} value={m.id}>
                      {m.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Chip
                size="small"
                label={`Config version: ${voiceRealtimeModelDraft.configVersion ?? 1}`}
              />
            </Box>
            {AccessControl.Access.editStudyLabGlobalModelsAccess && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  size="small"
                  disabled={savingVoiceRealtimeModels}
                  onClick={saveVoiceRealtimeModels}
                >
                  {savingVoiceRealtimeModels ? "Saving…" : "Save voice realtime models"}
                </Button>
              </Box>
            )}
          </Alert>
        </Box>

        {AccessControl.Access.listAiAgentAccess && (
          <>
            {selectedAgent ? (
              <AiAgentDetail
                agent={selectedAgent}
                access={AccessControl.Access}
                closeDetail={() => {
                  closeAiAgentDetail()
                  loadAiAgents()
                }}
                onEdit={(agent) => openDialog(agent)}
              />
            ) : (
              <AiAgentTable
                aiAgents={aiAgents}
                access={AccessControl.Access}
                tierPrograms={tierPrograms}
                products={products}
                onEdit={(agent) => openDialog(agent)}
                onDelete={loadAiAgents}
                onToggleStatus={loadAiAgents}
                onViewDetail={showAiAgentDetail}
              />
            )}
          </>
        )}
        {!AccessControl.Access.listAiAgentAccess && (
          <div className="page-empty">
            You do not have permission to view AI agent records.
          </div>
        )}
      </div>

      {aiAgentDialog}
      {aiAgentDialogLoading && <LoadingScreen />}
    </StyledAiAgent>
  )
}

export default AiAgent
